import { ExpandMore } from "@mui/icons-material"
import { Box, ButtonBase, Collapse, Stack, Typography } from "@mui/material"
import { useEffect, useState } from "react"

import Label from "@sap/components/label/Label"

import { sendFilterSectionToggleEvent } from "@/utils/posthog"

function CountBadge({ value }: { value: number | string }) {
  return (
    <Label
      sx={{
        height: "22px",
        minWidth: 24,
        padding: "2px 6px",
        cursor: "inherit",
        flexShrink: 0,
      }}
    >
      {value}
    </Label>
  )
}

export { CountBadge }

export default function FilterSection({
  title,
  children,
  defaultExpanded = false,
  activeCount = 0,
  trackingKey,
}: {
  title: string
  children: React.ReactNode
  defaultExpanded?: boolean
  activeCount?: number
  trackingKey?: string
}) {
  const collapseId = `filter-section-${title.toLowerCase().replace(/\s+/g, "-")}`
  const [expanded, setExpanded] = useState(defaultExpanded || activeCount > 0)

  useEffect(() => {
    if (activeCount > 0) {
      setExpanded(true)
    }
  }, [activeCount])

  const handleToggle = () => {
    const nextExpanded = !expanded

    if (trackingKey) {
      sendFilterSectionToggleEvent({
        section: trackingKey,
        isExpanded: nextExpanded,
      })
    }

    setExpanded(nextExpanded)
  }

  return (
    <Box
      sx={{
        borderTop: "1px solid",
        borderColor: "divider",
      }}
    >
      <ButtonBase
        onClick={handleToggle}
        disableRipple
        aria-expanded={expanded}
        aria-controls={collapseId}
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          paddingY: 1.5,
          paddingRight: 0.5,
          gap: 1,
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: "-2px",
            borderRadius: 1,
          },
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: "center",
          }}
        >
          <Typography
            variant="subtitle2"
            component="h3"
            sx={{
              fontWeight: 700,
              letterSpacing: "0.04em",
            }}
          >
            {title}
          </Typography>
          {activeCount > 0 && <CountBadge value={activeCount} />}
        </Stack>
        <ExpandMore
          sx={{
            fontSize: "1.25rem",
            flexShrink: 0,
            color: expanded ? "primary.main" : "text.secondary",
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 200ms ease, color 200ms ease",
          }}
        />
      </ButtonBase>
      <Collapse in={expanded} unmountOnExit={false}>
        <Box id={collapseId} sx={{ paddingBottom: 2 }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  )
}
